import type { ElMessageBoxOptions } from 'element-plus'
import type { AppContext, VNodeChild } from 'vue'
import { ElMessageBox } from 'element-plus'
import { h } from 'vue'

type ConfirmMethod = typeof ElMessageBox.confirm
type PatchedConfirmMethod = ConfirmMethod & {
  __appConfirmInstalled?: true
}

let originalConfirm: ConfirmMethod | undefined

function mergeClassName(baseClass: string, customClass?: string) {
  return [baseClass, customClass].filter(Boolean).join(' ')
}

function highlightConfirmData(message: ElMessageBoxOptions['message']) {
  if (typeof message !== 'string') {
    return message
  }

  const dataPattern = /「([^」]+)」|『([^』]+)』|"([^"]+)"|'([^']+)'/g
  const segments: VNodeChild[] = []
  let lastIndex = 0
  let match = dataPattern.exec(message)

  while (match) {
    const dataText = match[1] ?? match[2] ?? match[3] ?? match[4]
    const matchStart = match.index
    const dataStart = matchStart + match[0].indexOf(dataText)

    segments.push(message.slice(lastIndex, dataStart))
    segments.push(h('span', { class: 'app-confirm-message-box__data' }, dataText))
    lastIndex = dataStart + dataText.length
    match = dataPattern.exec(message)
  }

  if (!segments.length) {
    return message
  }

  segments.push(message.slice(lastIndex))
  return h('span', { class: 'app-confirm-message-box__text' }, segments)
}

function withAppConfirmStyle(options: ElMessageBoxOptions = {}): ElMessageBoxOptions {
  return {
    ...options,
    customStyle: {
      width: '440px',
      ...options.customStyle,
    },
    customClass: mergeClassName('app-confirm-message-box', options.customClass),
    cancelButtonClass: mergeClassName('app-confirm-message-box__cancel', options.cancelButtonClass),
    confirmButtonClass: mergeClassName('app-confirm-message-box__confirm', options.confirmButtonClass),
  }
}

/**
 * 与 ElMessageBox.confirm 保持相同的参数和 Promise 返回值，只统一确认框的样式类。
 */
export const appConfirm = ((
  message: ElMessageBoxOptions['message'],
  titleOrOptions?: string | ElMessageBoxOptions,
  optionsOrContext?: ElMessageBoxOptions | AppContext | null,
  appContext?: AppContext | null,
) => {
  const confirm = originalConfirm ?? ElMessageBox.confirm
  const styledMessage = highlightConfirmData(message)

  if (typeof titleOrOptions === 'string') {
    return confirm(
      styledMessage,
      titleOrOptions,
      withAppConfirmStyle((optionsOrContext as ElMessageBoxOptions | undefined) ?? {}),
      appContext,
    )
  }

  return confirm(
    styledMessage,
    withAppConfirmStyle(titleOrOptions ?? {}),
    optionsOrContext as AppContext | null | undefined,
  )
}) as ConfirmMethod

/**
 * 一次安装后，项目中现有和新增的 ElMessageBox.confirm 都自动使用统一样式。
 * prompt、alert 和直接调用 ElMessageBox 的业务弹窗不会受到影响。
 */
export function installAppConfirm() {
  const currentConfirm = ElMessageBox.confirm as PatchedConfirmMethod
  if (currentConfirm.__appConfirmInstalled) {
    return
  }

  originalConfirm = currentConfirm
  ;(appConfirm as PatchedConfirmMethod).__appConfirmInstalled = true
  ElMessageBox.confirm = appConfirm
}
